import { Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import TeacherShell, { TeacherPageHeader } from '@/components/layout/TeacherShell'
import { Card, CardContent, Button } from '@/components/ui'

const TYPE_LABELS = {
  vocabulary_size_test: 'Vocabulary Size Test',
  cloze_comprehension: 'Cloze Comprehension',
  writing_baseline: 'Writing Baseline',
  prior_knowledge: 'Prior Knowledge',
}

function StatusBadge({ status }) {
  const colors = {
    ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  }

  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || colors.draft}`}>
      {status}
    </span>
  )
}

export default function Index({ assessments }) {
  const { t } = useTranslation()
  return (
    <TeacherShell scrollTitle={t('teacherAssessments.title')}>
      <TeacherPageHeader title={t('teacherAssessments.title')} />

      {assessments.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-500 dark:text-gray-400">
            {t('teacherAssessments.noAssessments')} <code className="text-sm bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">{t('teacherAssessments.seedCommand')}</code> {t('teacherAssessments.seedSuffix')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {assessments.map((a) => (
            <Card key={a.id} className="flex flex-col">
              <CardContent className="flex-1 p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {a.title}
                  </h3>
                  <StatusBadge status={a.status} />
                </div>

                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  {TYPE_LABELS[a.assessment_type] || a.assessment_type}
                </p>

                {a.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                    {a.description}
                  </p>
                )}

                <div className="text-xs text-gray-400 dark:text-gray-500 space-y-0.5 mb-4">
                  <p>{a.item_count} {t('teacherAssessments.itemsInBank')}</p>
                  <p>{a.administration_count} {t('teacherAssessments.completedAdministrations')}</p>
                  {a.estimated_duration_minutes && <p>~{a.estimated_duration_minutes} min</p>}
                </div>

                <div className="flex gap-2 mt-auto">
                  <Link href={`/teacher/assessments/${a.id}`}>
                    <Button variant="secondary" size="sm">{t('common.view')}</Button>
                  </Link>
                  {a.status === 'ready' && (
                    <Link href={`/teacher/assessments/${a.id}/trial`} method="post" as="button">
                      <Button size="sm">{t('teacherAssessments.tryIt')}</Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </TeacherShell>
  )
}
